import React, { useEffect, useRef, useState, useCallback } from 'react'
import { ModalDeclinePayment } from '../ModalDeclinePayment'
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { acceptedPayment, getPaymentById, patchPayment } from '../../../redux/actions/payment';
import { requestCreateInvoice } from '../../../redux/actions/request';
import { getTotalRequestInvoicePending } from '../../../redux/actions/analytics';
import { formatDate, formatDateTime } from '../../../common/formatDateText';
import { ImageZoom } from '../../../components/imageZoom/ImageZoom';
import { LoaderSection } from '../../../components/LoaderSection';
import { InputDateModern } from '../../../components/inputDateModern/InputDateModern';
import { CounterModern } from '../../../components/counterModern';
import { Loading } from '../../../components/loading/Loading';
import { InputSelectAccountBank } from '../../../components/inputSelecList/InputSelectAccountBank';
import { InputCurrency } from '../../../components/inputs/InputCurrency';
import { PaymentHistoryTimeline } from '../../../components/paymentHistory/PaymentHistoryTimeline';

const PAYMENT_VALIDATION_FIELD_LABELS = {
    accountId: 'Cuenta bancaria',
    amount: 'Monto del pago',
    paymentReceiptDate: 'Fecha del recibo de pago',
    paymentDeadlineDate: 'Fecha de vencimiento de suscripción',
    paidWeeks: 'Semanas pagadas',
};

const parsePositiveAmount = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
};

export const TabPaymentDetails = ({
    idPayment,
    toggleDrawerFilters,
    updateDataTable,
    onValidatePayment,
    onDeclinePayment,
    onLoadingChange,
    isOpen,
    onEditModeChange,
    onSaveCallbackChange,
    onCancelCallbackChange,
    onLoadingSavingChange,
    loadingReceiptDateSaving,
    isReceiptAuxOpen = false,
}) => {
    const dispatch = useDispatch();

    const {
        paymentSelected,
        loadingSelectedPayment
    } = useSelector((state) => state.payment);

    const { user } = useSelector((state) => state.user);
    const { accountsBank } = useSelector((state) => state.options);
    const { _id: userId } = user;

    const { user: userPaymentSelected, validatedBy, paymentFolio, paymentType, createdAt, updatedAt, status, amount, account, history } = paymentSelected;

    const receiptImageUrl = (() => {
        const raw = paymentSelected?.urlName ?? paymentSelected?.url;
        if (raw == null || raw === '') return null;
        const s = String(raw).trim();
        return s || null;
    })();

    const [
        openDeclinePaymentModal,
        setOpenDeclinePaymentModal
    ] = useState(false);

    const [isValidating, setIsValidating] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);

    // Estado para controlar el acordeón del historial
    const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);

    const [validatedReceiptDate, setValidatedReceiptDate] = useState(null);
    const [receiptDateError, setReceiptDateError] = useState('');
    const [isEditingReceiptDate, setIsEditingReceiptDate] = useState(false);

    // Estado unificado del formulario de validación
    const [validationForm, setValidationForm] = useState({
        paymentReceiptDate: null,
        accountId: '',
        amount: 0,
        paidWeeks: 0,
        paymentDeadlineDate: null
    });

    const [formErrors, setFormErrors] = useState({
        accountId: '',
        amount: '',
        paymentReceiptDate: '',
        paymentDeadlineDate: '',
        paidWeeks: '',
    });

    const scrollContainerRef = useRef(null);
    const validationSummaryRef = useRef(null);
    const [showValidationSummary, setShowValidationSummary] = useState(false);

    const twoColDetailsGrid = 'grid grid-cols-1 md:grid-cols-2 gap-4';
    const oneColDetailsGrid = 'grid grid-cols-1 gap-4';
    const detailsGridClass = isReceiptAuxOpen ? oneColDetailsGrid : twoColDetailsGrid;
    const validationFormGridClass = isReceiptAuxOpen
        ? 'grid grid-cols-1 gap-x-4'
        : 'grid grid-cols-1 md:grid-cols-2 gap-x-4';

    // Resetear formulario cuando cambia el idPayment
    useEffect(() => {
        if (idPayment) {
            setValidationForm({
                paymentReceiptDate: null,
                accountId: '',
                amount: 0,
                paidWeeks: 0,
                paymentDeadlineDate: null
            });
            setFormErrors({
                accountId: '',
                amount: '',
                paymentReceiptDate: '',
                paymentDeadlineDate: '',
                paidWeeks: '',
            });
            setShowValidationSummary(false);
            setIsEditingReceiptDate(false);
            if (onEditModeChange) {
                onEditModeChange(false);
            }
        }
    }, [idPayment, onEditModeChange]);

    // Establecer valores iniciales cuando se carga el pago
    useEffect(() => {
        if (paymentSelected && status === 'pendiente') {
            setValidationForm(prev => ({
                ...prev,
                accountId: account?._id || '',
                amount: amount || 0
            }));
        }
    }, [paymentSelected, status, account, amount]);

    useEffect(() => {
        if (paymentSelected?.status === 'validado' && !isEditingReceiptDate) {
            const d = paymentSelected.paymentReceiptDate;
            setValidatedReceiptDate(d ? new Date(d) : null);
            setReceiptDateError('');
        }
    }, [
        paymentSelected?._id,
        paymentSelected?.status,
        paymentSelected?.paymentReceiptDate,
        isEditingReceiptDate,
    ]);

    // InputCurrency actualiza el estado sin pasar por updateFormField: limpiar error de monto al corregir
    useEffect(() => {
        if (validationForm.amount && Number(validationForm.amount) > 0) {
            setFormErrors(prev => ({ ...prev, amount: '' }));
        }
    }, [validationForm.amount]);

    // Prevenir scroll automático cuando cambia paidWeeks
    useEffect(() => {
        if (scrollContainerRef.current && validationForm.paidWeeks > 0) {
            const scrollTop = scrollContainerRef.current.scrollTop;
            requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollTop;
                }
            });
        }
    }, [validationForm.paidWeeks]);

    // Función para actualizar campos del formulario (limpia error del campo al editar)
    const updateFormField = useCallback((field, value) => {
        setValidationForm(prev => ({
            ...prev,
            [field]: value
        }));
        setFormErrors(prev => ({ ...prev, [field]: '' }));
    }, []);

    const handleCounterChange = useCallback((value) => {
        setFormErrors((prev) => ({ ...prev, paidWeeks: '' }));
        setValidationForm((prev) => ({
            ...prev,
            paidWeeks: value,
        }));
    }, []);

    // Notificar al componente padre sobre cambios en el estado de carga
    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(isValidating || isDeclining);
        }
    }, [isValidating, isDeclining, onLoadingChange]);

    const handleCancelReceiptDate = useCallback(() => {
        const d = paymentSelected?.paymentReceiptDate;
        setValidatedReceiptDate(d ? new Date(d) : null);
        setReceiptDateError('');
        setIsEditingReceiptDate(false);
        if (onEditModeChange) {
            onEditModeChange(false);
        }
    }, [paymentSelected?.paymentReceiptDate, onEditModeChange]);

    const handleSaveReceiptDate = useCallback(async () => {
        if (!validatedReceiptDate) {
            setReceiptDateError('Este campo es requerido');
            toast.error('Selecciona la fecha del recibo de pago.');
            return;
        }
        if (onLoadingSavingChange) {
            onLoadingSavingChange(true);
        }
        setReceiptDateError('');
        try {
            const result = await dispatch(
                patchPayment({
                    paymentId: idPayment,
                    paymentReceiptDate: validatedReceiptDate,
                    updatedBy: userId,
                })
            );
            if (result?.status === 200) {
                toast.success(result.message);
                setIsEditingReceiptDate(false);
                if (onEditModeChange) {
                    onEditModeChange(false);
                }
                if (updateDataTable) {
                    updateDataTable();
                }
            } else {
                toast.error(result?.message || 'No se pudo actualizar la fecha del recibo');
            }
        } finally {
            if (onLoadingSavingChange) {
                onLoadingSavingChange(false);
            }
        }
    }, [
        validatedReceiptDate,
        dispatch,
        idPayment,
        userId,
        updateDataTable,
        onLoadingSavingChange,
        onEditModeChange,
    ]);

    useEffect(() => {
        if (isOpen === false && isEditingReceiptDate) {
            handleCancelReceiptDate();
        }
    }, [isOpen, isEditingReceiptDate, handleCancelReceiptDate]);

    useEffect(() => {
        if (onEditModeChange) {
            onEditModeChange(isEditingReceiptDate);
        }
    }, [isEditingReceiptDate, onEditModeChange]);

    useEffect(() => {
        if (!onSaveCallbackChange || !onCancelCallbackChange) {
            return;
        }
        if (status !== 'validado') {
            onSaveCallbackChange(() => () => {});
            onCancelCallbackChange(() => () => {});
            return;
        }
        onSaveCallbackChange(() => handleSaveReceiptDate);
        onCancelCallbackChange(() => handleCancelReceiptDate);
    }, [
        status,
        idPayment,
        onSaveCallbackChange,
        onCancelCallbackChange,
        handleSaveReceiptDate,
        handleCancelReceiptDate,
    ]);

    const handleCancelValidationPayment = useCallback(async () => {
        setOpenDeclinePaymentModal(true);
        setIsDeclining(true);
    }, []);

    const validateData = useCallback(() => {
        const { accountId, amount, paidWeeks, paymentDeadlineDate, paymentReceiptDate } = validationForm;
        const nextErrors = {
            paymentDeadlineDate: !paymentDeadlineDate
                ? 'Selecciona la fecha de vencimiento de la suscripción.'
                : '',
            paymentReceiptDate: !paymentReceiptDate
                ? 'Selecciona la fecha del recibo de pago.'
                : '',
            paidWeeks: !paidWeeks || paidWeeks === 0
                ? 'Indica al menos 1 semana pagada.'
                : '',
            accountId: !accountId
                ? 'Selecciona la cuenta bancaria del comprobante.'
                : '',
            amount: !parsePositiveAmount(amount)
                ? 'Ingresa el monto del pago (debe ser mayor a $0).'
                : '',
        };
        setFormErrors(nextErrors);
        const isValid = !Object.values(nextErrors).some(Boolean);
        if (!isValid) {
            setShowValidationSummary(true);
            const missingLabels = Object.entries(nextErrors)
                .filter(([, msg]) => msg)
                .map(([key]) => PAYMENT_VALIDATION_FIELD_LABELS[key]);
            toast.error(
                missingLabels.length === 1
                    ? `Revisa: ${missingLabels[0]}`
                    : `Revisa: ${missingLabels.join(' · ')}`,
                { duration: 6000 },
            );
            requestAnimationFrame(() => {
                validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
            return false;
        }
        setShowValidationSummary(false);
        return true;
    }, [validationForm]);

    const handleValidationPayment = useCallback(async () => {
        if (!validateData()) {
            return;
        }

        setIsValidating(true);

        dispatch(acceptedPayment({
            paymentDeadlineDate: validationForm.paymentDeadlineDate,
            paymentReceiptDate: validationForm.paymentReceiptDate,
            email: paymentSelected.user?.email,
            paymentId: idPayment,
            userId: paymentSelected.user?._id,
            updatedBy: userId,
            validatedBy: userId,
            paidWeeks: validationForm.paidWeeks,
            accountId: validationForm.accountId,
            amount: validationForm.amount
        }))
            .then((result) => {
                if (result.status === 200) {
                    toast.success(result.message);
                    // Crear solicitud de factura
                    dispatch(requestCreateInvoice(paymentSelected?.user?._id, idPayment))
                        .then((invoiceResult) => {
                            if (invoiceResult?.status === 201) {
                                toast.success(invoiceResult.message);
                                dispatch(getTotalRequestInvoicePending('pending'));
                            }
                        });

                    setIsValidating(false);

                    // Recargar los datos del pago actualizado
                    dispatch(getPaymentById(idPayment));

                    // Actualizar la tabla de pagos
                    if (updateDataTable) {
                        updateDataTable();
                    }
                } else {
                    toast.error(result.message);
                    setIsValidating(false);
                }
            })
            .catch((error) => {
                toast.error('Error al validar el pago');
                setIsValidating(false);
            });
    }, [validateData, validationForm, paymentSelected, idPayment, userId, dispatch, updateDataTable]);


    const handleDeclineModalClose = useCallback(() => {
        setIsDeclining(false);
    }, []);

    // Exponer las funciones al componente padre
    useEffect(() => {
        if (onValidatePayment) {
            onValidatePayment(handleValidationPayment);
        }
        if (onDeclinePayment) {
            onDeclinePayment(handleCancelValidationPayment);
        }
    }, [onValidatePayment, onDeclinePayment, handleValidationPayment, handleCancelValidationPayment]);

    const comprobanteInner = (
        <>
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <h4 className="text-lg font-bold text-gray-800">Comprobante de Pago</h4>
            </div>
            <div className='w-full flex flex-col justify-center items-center py-4 border-dashed border-spacing-4 border-2 border-gray-200 rounded-lg'>
                {loadingSelectedPayment && (
                    <div className='w-full flex justify-center items-center min-h-[280px] md:min-h-[400px]'>
                        <LoaderSection />
                    </div>
                )}
                {!loadingSelectedPayment && receiptImageUrl && (
                    <ImageZoom
                        key={paymentSelected?._id || idPayment}
                        imageUrl={receiptImageUrl}
                    />
                )}
                {!loadingSelectedPayment && !receiptImageUrl && (
                    <div className='w-full flex justify-center items-center min-h-[280px] md:min-h-[400px] px-4'>
                        <span className='text-gray-600 text-center'>
                            No se ha subido un comprobante de pago
                        </span>
                    </div>
                )}
            </div>
        </>
    );

    return (
        loadingSelectedPayment ? (
            <Loading />
        ) : (
            <div className="h-full flex flex-col">
                <div className="flex-shrink-0 mb-6">
                    <div className="bg-white border border-gray-200 rounded-lg px-5 py-3 lg:py-4 shadow-sm">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900">
                                        Detalles del pago
                                    </h3>
                                    <p className="text-xs text-gray-500 font-medium mt-0.5 hidden md:block">
                                        Se muestra los detalles del pago
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {status === 'validado' && !isEditingReceiptDate && (
                                    <button
                                        type="button"
                                        onClick={() => setIsEditingReceiptDate(true)}
                                        disabled={loadingSelectedPayment || loadingReceiptDateSaving}
                                        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Editar
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex-1 overflow-hidden pr-2 min-h-0 flex flex-col'>
                    <div className='flex-1 min-h-0 flex gap-4'>
                        {isReceiptAuxOpen && (
                            <div className='hidden h-full min-h-0 shrink-0 self-stretch xl:flex'>
                                <div className='flex h-full min-h-0 w-[25vw] flex-col rounded-lg bg-white'>
                                    <div className='min-h-0 flex-1 overflow-auto'>
                                        <div className='bg-white border border-gray-200 rounded-lg p-5'>
                                            {comprobanteInner}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className='flex-1 min-w-0 min-h-0 overflow-y-auto' ref={scrollContainerRef}>
                    {/* Información del Usuario */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <h4 className="text-lg font-bold text-gray-800">Información del Usuario</h4>
                        </div>
                        <div className={detailsGridClass}>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Correo Electrónico</label>
                                <p className="text-[.9rem] font-medium text-gray-900 break-words">{userPaymentSelected?.email || '-'}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Fecha de Vencimiento</label>
                                <p className="text-[.9rem] font-medium text-gray-900 uppercase">{formatDate(userPaymentSelected?.paymentDeadlineDate) || '-'}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Monto de la Suscripción</label>
                                <p className="text-[.9rem] font-medium text-gray-900">{`$ ${userPaymentSelected?.subscriptionPayment || 0}`}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Estado de Suscripción</label>
                                <div className="flex items-center gap-2">
                                    {userPaymentSelected?.statusSubscription === 'pagado' && (
                                        <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Pagado
                                        </span>
                                    )}
                                    {userPaymentSelected?.statusSubscription === 'vencida' && (
                                        <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg font-semibold text-sm">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                            Vencida
                                        </span>
                                    )}
                                    {!userPaymentSelected?.statusSubscription && <span className="text-[.9rem] font-medium text-gray-900">-</span>}
                                </div>
                            </div>
                            {user?.statusSubscription === 'pagado' && (
                                <>
                                    <div className="space-y-1">
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Último Monto Pagado</label>
                                        <p className="text-[.9rem] font-medium text-gray-900">{`$ ${userPaymentSelected?.lastPaymentAmount || 0}`}</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Semanas Pagadas</label>
                                        <p className="text-[.9rem] font-medium text-gray-900 capitalize">{userPaymentSelected?.paidWeeks || 0}</p>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Información del Pago */}
                    <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                            <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <h4 className="text-lg font-bold text-gray-800">Información del Pago</h4>
                        </div>
                        <div className={detailsGridClass}>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Folio</label>
                                <p className="text-[.9rem] font-medium text-gray-900">{`#${paymentFolio || '-'}`}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Tipo de Pago</label>
                                <p className="text-[.9rem] font-medium text-gray-900 uppercase">{paymentType || '-'}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Fecha de Pago</label>
                                <p className="text-[.9rem] font-medium text-gray-900 uppercase">{formatDateTime(createdAt) || '-'}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Fecha Última Actualización</label>
                                <p className="text-[.9rem] font-medium text-gray-900 uppercase">{formatDateTime(updatedAt) || '-'}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Estatus del Pago</label>
                                <div className="flex items-center gap-2">
                                    {status === 'validado' && (
                                        <span className="inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Validado
                                        </span>
                                    )}
                                    {status === 'pendiente' && (
                                        <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Pendiente
                                        </span>
                                    )}
                                    {status === 'rechazado' && (
                                        <span className="inline-flex items-center gap-1.5 bg-rose-100 text-rose-700 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Rechazado
                                        </span>
                                    )}
                                    {!status && <span className="text-[.9rem] font-medium text-gray-900">-</span>}
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Cantidad Depositada</label>
                                <p className="text-[.9rem] font-medium text-gray-900">{`$ ${amount || 0}`}</p>
                            </div>
                            {status === 'validado' && (
                                <div className="space-y-1 md:col-span-2">
                                    <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">
                                        Fecha del Recibo de Pago
                                    </label>
                                    {isEditingReceiptDate ? (
                                        <div className="mt-1 max-w-md">
                                            <InputDateModern
                                                id="paymentReceiptDateValidated"
                                                value={validatedReceiptDate}
                                                onChange={(date) => {
                                                    setValidatedReceiptDate(date);
                                                    setReceiptDateError('');
                                                }}
                                                label=""
                                                placeholder="Fecha del recibo de pago"
                                                disabled={loadingReceiptDateSaving || loadingSelectedPayment}
                                                required
                                                error={receiptDateError}
                                            />
                                        </div>
                                    ) : (
                                        <p className="text-[.9rem] font-medium text-gray-900 uppercase">
                                            {validatedReceiptDate
                                                ? formatDate(validatedReceiptDate) || '-'
                                                : '-'}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Historial de Movimientos - Acordeón */}
                        {history && history.length > 0 && (
                            <div className="mt-6 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                                    className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                                >
                                    <div className="flex items-center gap-2">
                                        <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-base font-bold text-gray-800">Historial de Movimientos</span>
                                        <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full">
                                            {history.length}
                                        </span>
                                    </div>
                                    <svg
                                        className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isHistoryExpanded ? 'rotate-180' : ''
                                            }`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {/* Contenido del acordeón */}
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${isHistoryExpanded ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                                        }`}
                                >
                                    <PaymentHistoryTimeline history={history} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Información de la Cuenta - Solo cuando está validado */}
                    {status === 'validado' && (
                        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Información de la Cuenta</h4>
                            </div>
                            <div className={detailsGridClass}>
                                <div className="space-y-1">
                                    <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Nombre de la Cuenta</label>
                                    <p className="text-[.9rem] font-medium text-gray-900 uppercase">{account?.nameAccount || '-'}</p>
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Número de Cuenta</label>
                                    <p className="text-[.9rem] font-medium text-gray-900 uppercase">{account?.numberAccount || '-'}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Validación */}
                    {status === 'validado' && (
                        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0121 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2c1.821 0 3.53.456 5.03 1.26" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Validación</h4>
                            </div>
                            <div className="grid grid-cols-1 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide">Validado Por</label>
                                    <p className="text-[.9rem] font-medium text-gray-900 capitalize">
                                        {`${validatedBy?.firstName || ''} ${validatedBy?.secondName || ''} ${validatedBy?.lastName || ''} ${validatedBy?.secondSurname || ''}`.trim() || '-'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Comprobante: en pantallas xl+ se oculta aquí si el panel auxiliar está abierto; en móvil/tablet siempre visible en esta columna. */}
                    <div
                        className={`mb-5 rounded-lg border border-gray-200 bg-white p-5 ${
                            isReceiptAuxOpen ? 'block xl:hidden' : ''
                        }`}
                    >
                        {comprobanteInner}
                    </div>

                    {/* Configuración de Validación del Pago - Solo visible cuando el pago está pendiente */}
                    {status === 'pendiente' && !loadingSelectedPayment && (
                        <div className="bg-white border border-gray-200 rounded-lg p-5 mb-5">
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                                <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0121 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2c1.821 0 3.53.456 5.03 1.26" />
                                </svg>
                                <h4 className="text-lg font-bold text-gray-800">Configuración de Validación del Pago</h4>
                            </div>
                            <div className={validationFormGridClass}>
                                {/* Cuenta Bancaria */}
                                <div className="my-4 md:my-6">
                                    <InputSelectAccountBank
                                        name="accountBank"
                                        label="Cuenta bancaria"
                                        placeholder="Cuenta bancaria"
                                        value={validationForm.accountId}
                                        onChange={(e) => updateFormField('accountId', e.target.value)}
                                        options={accountsBank}
                                        disabled={loadingSelectedPayment}
                                        required
                                        error={formErrors.accountId}
                                    />
                                </div>

                                {/* Monto del Pago */}
                                <div className="my-4 md:my-6">
                                    <InputCurrency
                                        name="amount"
                                        label="Monto del pago"
                                        placeholder="$ 0.00"
                                        formData={validationForm}
                                        setFormData={setValidationForm}
                                        disabled={loadingSelectedPayment}
                                        required
                                        error={formErrors.amount}
                                    />
                                </div>

                                {/* Fecha del recibo de pago */}
                                <div className="my-4 md:my-6">
                                    <InputDateModern
                                        id="paymentReceiptDate"
                                        value={validationForm.paymentReceiptDate}
                                        onChange={(date) => updateFormField('paymentReceiptDate', date)}
                                        label="Fecha del recibo de pago"
                                        placeholder="Fecha del recibo de pago"
                                        disabled={loadingSelectedPayment}
                                        required
                                        error={formErrors.paymentReceiptDate}
                                    />
                                </div>
                                {/* Fecha de Vencimiento */}
                                <div className="my-4 md:my-6">
                                    <InputDateModern
                                        id="dateExpired"
                                        value={validationForm.paymentDeadlineDate}
                                        onChange={(date) => updateFormField('paymentDeadlineDate', date)}
                                        label="Fecha de vencimiento de suscripción"
                                        placeholder="Fecha de vencimiento de suscripción"
                                        minDate={new Date()}
                                        disabled={loadingSelectedPayment}
                                        required
                                        error={formErrors.paymentDeadlineDate}
                                    />
                                </div>
                                <div className="my-4 md:my-6">
                                    <CounterModern
                                        id="paidWeeks"
                                        label="Semanas pagadas"
                                        required
                                        value={validationForm.paidWeeks}
                                        onChange={handleCounterChange}
                                        min={0}
                                        disabled={loadingSelectedPayment}
                                        error={formErrors.paidWeeks}
                                    />
                                </div>
                            </div>
                            {showValidationSummary && Object.values(formErrors).some(Boolean) && (
                                <div
                                    ref={validationSummaryRef}
                                    className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4"
                                    role="alert"
                                >
                                    <p className="text-sm font-semibold text-red-800 mb-2">
                                        Corrige los siguientes campos para validar el pago:
                                    </p>
                                    <ul className="list-disc space-y-1.5 pl-5 text-sm text-red-700">
                                        {Object.entries(formErrors)
                                            .filter(([, msg]) => msg)
                                            .map(([key, msg]) => (
                                                <li key={key}>
                                                    <span className="font-semibold">
                                                        {PAYMENT_VALIDATION_FIELD_LABELS[key]}:
                                                    </span>{' '}
                                                    {msg}
                                                </li>
                                            ))}
                                    </ul>
                                </div>
                            )}
                            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800 text-center">
                                    <svg className="w-4 h-4 inline-block mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Los campos marcados con * son obligatorios para validar el pago del estudiante
                                </p>
                            </div>
                        </div>
                    )}
                        </div>
                    </div>
                </div>
                <ModalDeclinePayment
                    open={openDeclinePaymentModal}
                    setOpen={setOpenDeclinePaymentModal}
                    paymentId={idPayment}
                    toggleDrawerFilters={toggleDrawerFilters}
                    updateDataTable={updateDataTable}
                    onClose={handleDeclineModalClose}
                />
            </div>
        )
    )
}